import { useSelector } from "react-redux";
import { DoneIcon, AddIcon, RemoveIcon } from "../components/icons";
import toPriceDisplay from "../utils/toPriceDisplay";
import addLineBreaks from "../utils/addLineBreaks";
import ObservationsCard from "./ObservationsCard";
import useQuestionState from "../hooks/useQuestionState";
import { questionHelpText } from "../utils/questions";

// O rodapé do produto rola até a pergunta obrigatória pendente por este id.
export const questionSectionId = (questionId) => `pergunta-${questionId}`;

export default function QuestionsCards({
  isAvailableToOrder,
  productId,
  orderUuid,
}) {
  const questions = useSelector(
    (state) => state.products.questionsByProduct[productId],
  );

  return (
    <div className="question-list">
      {questions.map((question) => (
        <QuestionCard
          key={question.id}
          isAvailableToOrder={isAvailableToOrder}
          question={question}
          orderUuid={orderUuid}
        />
      ))}
      <ObservationsCard
        orderUuid={orderUuid}
        isAvailableToOrder={isAvailableToOrder}
      />
    </div>
  );
}

const QuestionCard = ({ isAvailableToOrder, question, orderUuid }) => {
  const { description, minQtt, maxQtt } = question;
  const { qtt, isSingleChoice, isCombo, isIncrementDisabled, statefulOptions } =
    useQuestionState(question, orderUuid);

  return (
    <section
      id={questionSectionId(question.id)}
      className="question"
      aria-labelledby={`${questionSectionId(question.id)}-title`}
    >
      <QuestionHeader
        titleId={`${questionSectionId(question.id)}-title`}
        description={description}
        helpText={questionHelpText(question)}
        isCombo={isCombo}
        minQtt={minQtt}
        maxQtt={maxQtt}
        qtt={qtt}
      />
      {question.options.length > 0 && (
        <OptionsList
          isAvailableToOrder={isAvailableToOrder}
          question={question}
          isSingleChoice={isSingleChoice}
          isIncrementDisabled={isIncrementDisabled}
          statefulOptions={statefulOptions}
        />
      )}
    </section>
  );
};

/**
 * Cabeçalho da pergunta. Fica preso no topo enquanto as opções dela rolam,
 * para o cliente não perder de vista quantas ainda faltam.
 */
const QuestionHeader = ({
  titleId,
  description,
  helpText,
  isCombo,
  minQtt,
  maxQtt,
  qtt,
}) => {
  const isRequired = minQtt > 0;
  const isDone = isRequired && qtt >= minQtt;

  return (
    <header className="question__header">
      <div className="question__heading">
        <h2 id={titleId} className="question__title">
          {description}
        </h2>
        <p className="question__help">{helpText}</p>
      </div>
      {!isCombo && (
        <div className="question__status">
          {maxQtt > 1 && (
            <span
              className={`question__counter${isDone ? " is-done" : ""}`}
              aria-label={`${qtt} de ${maxQtt} escolhidas`}
            >
              {qtt}/{maxQtt}
            </span>
          )}
          {isRequired && !isDone && (
            <span className="tag tag--required">Obrigatório</span>
          )}
          {isDone && (
            <span className="tag tag--done" aria-label="Concluído">
              <DoneIcon fontSize="inherit" />
            </span>
          )}
        </div>
      )}
    </header>
  );
};

const OptionsList = ({
  isAvailableToOrder,
  question,
  isSingleChoice,
  isIncrementDisabled,
  statefulOptions,
}) => {
  const { id: questionId, isAllowedOptionRepetition } = question;

  return (
    <ul className="option-list">
      {statefulOptions.map((option) => (
        <li key={option.id}>
          {option.comboItem ? (
            <OptionRow option={option} />
          ) : (
            <Option
              questionId={questionId}
              isAllowedOptionRepetition={isAllowedOptionRepetition}
              isAvailableToOrder={isAvailableToOrder}
              isSingleChoice={isSingleChoice}
              isIncrementDisabled={isIncrementDisabled}
              option={option}
            />
          )}
        </li>
      ))}
    </ul>
  );
};

/**
 * Uma opção. Radio e checkbox são <label> em volta do input nativo: a linha
 * inteira responde ao toque, e leitores de tela anunciam o controle certo.
 * Opção com repetição (2x, 3x a mesma) tem −/+ em vez de marcação.
 */
const Option = ({
  questionId,
  isAllowedOptionRepetition,
  isAvailableToOrder,
  isSingleChoice,
  isIncrementDisabled,
  option,
}) => {
  const inputId = `question-${questionId}-option-${option.id}`;
  const isInteractive = isAvailableToOrder && !option.disabled;

  if (!isInteractive) {
    return <OptionRow option={option} />;
  }

  if (isAllowedOptionRepetition) {
    return (
      <OptionRow option={option} selected={option.answeredQtt > 0}>
        <div className="qty-stepper qty-stepper--compact">
          {option.answeredQtt > 0 && (
            <>
              <button
                type="button"
                className="icon-button"
                onClick={option.onDecrementClick}
                aria-label={`Tirar um ${option.label}`}
              >
                <RemoveIcon fontSize="small" />
              </button>
              <span className="qty-stepper__value">{option.answeredQtt}</span>
            </>
          )}
          <button
            type="button"
            className="icon-button"
            onClick={option.onIncrementClick}
            disabled={isIncrementDisabled}
            aria-label={`Adicionar um ${option.label}`}
          >
            <AddIcon fontSize="small" />
          </button>
        </div>
      </OptionRow>
    );
  }

  // Com o máximo atingido, as opções não marcadas ficam desabilitadas (antes
  // o checkbox sumia, e parecia que a opção não existia mais).
  const isBlocked = !isSingleChoice && isIncrementDisabled && !option.isChecked;

  return (
    <OptionRow
      as="label"
      htmlFor={inputId}
      option={option}
      selected={option.isChecked}
      blocked={isBlocked}
    >
      <input
        id={inputId}
        type={isSingleChoice ? "radio" : "checkbox"}
        className="option__control"
        checked={option.isChecked}
        disabled={isBlocked}
        // onClick, e não onChange: o radio marcado precisa poder ser
        // desmarcado com um novo toque, e aí o onChange não dispara
        onClick={isSingleChoice ? option.onRadioClick : option.onCheckboxClick}
        onChange={() => {}}
      />
    </OptionRow>
  );
};

const OptionRow = ({
  as: Tag = "div",
  option,
  selected = false,
  blocked = false,
  children,
  ...rest
}) => {
  const className = [
    "option",
    selected ? "is-selected" : "",
    blocked || option.disabled ? "is-disabled" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Tag className={className} {...rest}>
      {option.pictureUrl && (
        <img
          src={option.pictureUrl}
          alt=""
          loading="lazy"
          decoding="async"
          width="56"
          height="56"
          className="option__img"
        />
      )}
      <span className="option__body">
        <span className="option__label">{option.label}</span>
        {option.details && (
          <span className="option__details">
            {addLineBreaks(option.details)}
          </span>
        )}
        {option.disabled && (
          <span className="tag tag--muted option__tag">Indisponível</span>
        )}
        {!option.disabled && !!parseFloat(option.price) && (
          <span className="option__price">
            + {toPriceDisplay(option.price)}
          </span>
        )}
      </span>
      {children}
    </Tag>
  );
};
